import React from 'react';
import { Box, useTheme, useMediaQuery, IconButton, Typography } from '@mui/material';
import { ArrowLeft } from 'lucide-react';

interface MessageLayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
}

interface MessageLayoutComposition {
  Sidebar: React.FC<{ children: React.ReactNode }>;
  Content: React.FC<{ children: React.ReactNode }>;
}

export const MessageLayout: React.FC<MessageLayoutProps> & MessageLayoutComposition = ({ 
  children,
  showBackButton,
  onBack,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box 
      sx={{ 
        display: 'grid',
        gridTemplateColumns: { 
          xs: '1fr', 
          md: '320px 1fr' 
        },
        gridTemplateRows: { 
          xs: showBackButton ? '48px 1fr' : '1fr', 
          md: '1fr' 
        },
        gap: 3,
        height: { 
          xs: 'calc(100vh - 180px)', 
          md: 'calc(100vh - 280px)' 
        },
        position: 'relative',
      }}
    >
      {isMobile && showBackButton && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          px: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
          <IconButton onClick={onBack} edge="start">
            <ArrowLeft />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Back to Messages
          </Typography>
        </Box>
      )}
      {children}
    </Box>
  );
};

MessageLayout.Sidebar = ({ children }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 3,
      height: '100%',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

MessageLayout.Content = ({ children }) => (
  <Box 
    sx={{ 
      height: '100%',
      minHeight: { xs: 400, md: 'auto' },
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {children}
  </Box>
);